import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ActionLogsDateRangeSelector from './ActionLogsDateRangeSelector';
import ActionLogsDateRangePicker from './ActionLogsDateRangePicker';
import { useActionLogsPageQueryParams } from '../../../hooks/useActionLogsPageQueryParams';
import { useActionLogsPageFilterState } from '../../../hooks/useActionLogsPageFilterState';
import { FilterValues, PredefinedDateRange, DateRangeValue } from '../../../types/actionLogsTypes';
import { formatForURL, toUTCISOString, validateDates } from '../../../utils/dateUtils';

const PREDEFINED_DATE_RANGES: PredefinedDateRange[] = [
  { value: 'day', label: 'Last day' },
  { value: 'week', label: 'Last week' },
  { value: 'month', label: 'Last month' },
  { value: 'year', label: 'Last year' },
  { value: 'beginning', label: 'From beginning' },
];

interface ActionLogsPageFilterProps {
  onApply: (filters: FilterValues) => void;
}

const ActionLogsPageFilter: React.FC<ActionLogsPageFilterProps> = ({ onApply }) => {
  const { setParams } = useActionLogsPageQueryParams();
  const { range, setRange, start, setStart, end, setEnd } = useActionLogsPageFilterState();

  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    if (range === 'custom') {
      const validationError = validateDates(start, end);
      if (validationError) {
        setError(validationError);
        setOpenSnackbar(true);
      } else if (start && end) {
        onApply({
          started_at: toUTCISOString(start),
          ended_at: toUTCISOString(end),
        });
      }
    } else {
      onApply({ range });
    }
  }, []);

  const handleRangeChange = (value: DateRangeValue) => {
    setError(null);
    setRange(value);

    if (value !== 'custom') {
      setStart(null);
      setEnd(null);
      onApply({ range: value });
      setParams({ range: value, start: undefined, end: undefined });
    } else {
      setParams({
        range: 'custom',
        start: start ? formatForURL(start) : undefined,
        end: end ? formatForURL(end) : undefined,
      });
    }
  };

  const handleApply = () => {
    const validationError = validateDates(start, end);
    if (validationError) {
      setError(validationError);
      setOpenSnackbar(true);
      return;
    }

    onApply({
      started_at: toUTCISOString(start!),
      ended_at: toUTCISOString(end!),
    });
    setParams({
      range: 'custom',
      start: formatForURL(start),
      end: formatForURL(end),
    });
  };

  return (
    <>
      <Box paddingBottom={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <ActionLogsDateRangeSelector
            value={range}
            onChange={handleRangeChange}
            predefinedDateRanges={PREDEFINED_DATE_RANGES}
          />
          {range === 'custom' && (
            <ActionLogsDateRangePicker
              startDate={start}
              endDate={end}
              onStartDateChange={setStart}
              onEndDateChange={setEnd}
              onApply={handleApply}
            />
          )}
        </Stack>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ActionLogsPageFilter;
